import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, Zap, Droplet, ChevronRight, ChevronDown,
  FolderOpen, Folder, Loader2, Search, FileText, Users,
} from 'lucide-react';
import * as api from '../services/analysisService';
import { downloadSinglePdf } from '../features/pdf/pdfExport';
import { AnalysisResult } from '../components/dashboard/AnalysisResult';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { formatNumber, formatPeriod, formatEuro } from '../utils/formatters';

/**
 * Pagina di SUPERVISIONE (solo admin): vista in SOLA LETTURA di tutte le analisi
 * finalizzate di ogni account, raggruppate per agente/dipendente. Nessuna modifica,
 * finalizzazione o eliminazione: l'admin può solo consultare e scaricare i PDF.
 */
export default function SupervisionPage() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [openFolders, setOpenFolders] = useState({});
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const analysisRef = useRef(null);

  useEffect(() => {
    api.fetchAllAnalyses()
      .then((data) => setAnalyses(data))
      .catch(() => setError('Errore nel caricamento delle analisi.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return analyses;
    return analyses.filter((a) => {
      const cd = a.competitor_data || {};
      return (
        (cd.client_name || '').toLowerCase().includes(q) ||
        (cd.sii_code || '').toLowerCase().includes(q) ||
        (a.owner_name || '').toLowerCase().includes(q) ||
        (a.owner_email || '').toLowerCase().includes(q)
      );
    });
  }, [analyses, query]);

  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((a) => {
      const key = a.owner_email || a.owner_name || 'sconosciuto';
      if (!map[key]) map[key] = { key, label: a.owner_name || 'Sconosciuto', role: a.owner_role, analyses: [] };
      map[key].analyses.push(a);
    });
    return Object.values(map).sort((x, y) => x.label.localeCompare(y.label));
  }, [filtered]);

  const handleView = (a) => {
    setError('');
    setSelected({ ...a, is_volatile: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadPdf = async () => {
    if (!selected || !analysisRef.current) return;
    setDownloading(true);
    try {
      await downloadSinglePdf(analysisRef.current, selected);
    } catch (e) {
      setError('Errore nella generazione del PDF');
    }
    setDownloading(false);
  };

  return (
    <div className="min-h-screen bg-[#F4F7F4]">
      <DashboardHeader section={{ icon: Eye, label: 'Supervisione', hint: 'Sola lettura · tutti gli account' }} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600" data-testid="supervision-error">
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Colonna sinistra — elenco raggruppato per agente */}
          <div className="lg:col-span-5 space-y-4">
            <div className="el-card p-4">
              <div className="relative">
                <Search className="w-4 h-4 text-[#5C6E5C] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca agente, cliente o POD/PDR"
                  className="el-input w-full pl-9"
                  data-testid="supervision-search"
                />
              </div>
            </div>

            {loading ? (
              <div className="el-card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#4CAF50]" /></div>
            ) : groups.length === 0 ? (
              <div className="el-card p-10 text-center" data-testid="supervision-empty">
                <Users className="w-12 h-12 mx-auto text-[#DCE3DC] mb-3" />
                <p className="text-[#5C6E5C]">Nessuna analisi trovata.</p>
              </div>
            ) : (
              <div className="el-card p-4 space-y-2" data-testid="supervision-list">
                {groups.map((group) => {
                  const isOpen = !!openFolders[group.key];
                  return (
                    <div key={group.key} className="border border-[#DCE3DC] rounded-xl overflow-hidden" data-testid={`supervision-folder-${group.key}`}>
                      <button
                        onClick={() => setOpenFolders((p) => ({ ...p, [group.key]: !p[group.key] }))}
                        className="w-full flex items-center gap-3 p-3 bg-[#F4F7F4] hover:bg-[#E8F5E9] transition-colors text-left"
                        data-testid={`supervision-folder-toggle-${group.key}`}
                      >
                        {isOpen ? <FolderOpen className="w-5 h-5 text-[#4CAF50] flex-shrink-0" /> : <Folder className="w-5 h-5 text-[#5C6E5C] flex-shrink-0" />}
                        <span className="font-medium text-sm text-[#1B291B] flex-1">
                          {group.label}
                          {group.role === 'admin' && <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">Admin</span>}
                        </span>
                        <span className="text-xs text-[#5C6E5C] bg-white px-2 py-0.5 rounded-full border border-[#DCE3DC]">{group.analyses.length}</span>
                        <ChevronDown className={`w-4 h-4 text-[#5C6E5C] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>

                      <AnimatePresence>
                        {isOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }} className="overflow-hidden">
                            <div className="p-2 space-y-2 bg-white">
                              {group.analyses.map((a) => (
                                <SupervisionItem
                                  key={a.id}
                                  analysis={a}
                                  isViewing={selected?.id === a.id}
                                  onView={handleView}
                                />
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Colonna destra — dettaglio analisi (sola lettura) */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {selected ? (
                <motion.div key={selected.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
                  <AnalysisResult
                    analysisRef={analysisRef}
                    analysis={selected}
                    isHistorical={true}
                    downloading={downloading}
                    onDownloadPdf={handleDownloadPdf}
                  />
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="el-card p-12 text-center" data-testid="supervision-empty-detail">
                  <FileText className="w-16 h-16 mx-auto text-[#DCE3DC] mb-4" />
                  <h3 className="text-xl font-medium text-[#1B291B] mb-2">Seleziona un'analisi</h3>
                  <p className="text-[#5C6E5C] max-w-md mx-auto">Scegli un agente e apri un'analisi per consultarla in sola lettura.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Riga analisi in sola lettura */
const SupervisionItem = ({ analysis: a, isViewing, onView }) => {
  const isAnnual = !!a.annual_projection;
  const positive = isAnnual ? a.annual_projection.is_positive : a.is_positive;
  const pct = isAnnual ? a.annual_projection.savings_annual_percent : a.savings_percent;
  const amt = isAnnual ? a.annual_projection.savings_annual : a.savings_amount;
  const suffix = isAnnual ? '/anno' : '';
  const sign = positive ? '-' : '+';
  return (
    <div
      onClick={() => onView(a)}
      className={`p-3 rounded-lg border transition-all cursor-pointer ${
        isViewing ? 'border-[#4CAF50] bg-[#E8F5E9]/50' : 'border-[#DCE3DC] hover:border-[#4CAF50]/50'
      }`}
      data-testid={`supervision-item-${a.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {a.competitor_data?.service_type === 'gas'
            ? <Droplet className="w-5 h-5 text-sky-400 fill-sky-400" />
            : <Zap className="w-5 h-5 text-yellow-500" />}
          <div>
            <p className="font-medium text-[#1B291B] text-sm">{a.competitor_data?.client_name || 'N/A'}</p>
            <p className="text-xs text-[#5C6E5C] mt-0.5">vs {(a.competitor_data?.supplier_name || '').toUpperCase()}</p>
            <p className="text-xs text-[#5C6E5C] mt-0.5">
              {a.competitor_data?.consumption ? formatNumber(a.competitor_data.consumption, 0) : '?'} {a.competitor_data?.consumption_unit || ''}
              {a.competitor_data?.billing_period_start && (
                <> &bull; {formatPeriod(a.competitor_data.billing_period_start, a.competitor_data.billing_period_end)}</>
              )}
              {a.competitor_data?.sii_code && (
                <> &bull; <span className="font-medium">{a.competitor_data?.service_type === 'gas' ? 'PDR' : 'POD'}:</span> {a.competitor_data.sii_code}</>
              )}
            </p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[10px] bg-[#F4F7F4] text-[#5C6E5C] px-1.5 py-0.5 rounded">{a.selected_cte}</span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${positive ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                {sign}{formatNumber(Math.abs(pct), 1)}%{suffix}
              </span>
              {amt != null && (
                <span className={`text-[10px] font-semibold ${positive ? 'text-green-700' : 'text-orange-700'}`}>
                  {sign}{formatEuro(Math.abs(amt))}{suffix}
                </span>
              )}
            </div>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#5C6E5C] mt-1" />
      </div>
    </div>
  );
};
